/**
 * 实时语音识别客户端(web 版)—— 复刻小程序 utils/asr-realtime-aliyun.js。
 *
 * 本地 VAD 门控:麦克风一直开(getUserMedia + AudioWorklet 出 16k Int16 帧),但 WS 不常连——
 * 连续 N 帧 RMS 高过自适应阈值才"开口"→ 连 WS 上传;连续 N 帧低过阈值"说完"→ 发 stop。
 * 开口前 ~500ms 由 pre-roll 环形缓冲补回(避免吞首字)。
 *
 * 与小程序的差异(平台不同,行为一致):
 *   - 录音:getUserMedia + AudioContext(16k) + worklet,替代 wx.getRecorderManager
 *   - WS:连我们的 server 代理 /api/asr/ws(浏览器不能直连 DashScope),协议简化为
 *     发二进制 PCM 帧 + {type:'stop'},收 {type:'partial'|'final'|'turn_done'|'error'}。
 *     run-task/finish-task 由服务端独占,所以客户端不等 task-started、WS open 即 drain。
 *
 * 对外:new RealtimeASR({engine}).on('partial'|'final'|'error'|'close', fn); start(); abort()
 */

import { api } from './api'

type ASREvent = 'partial' | 'final' | 'error' | 'close' | 'capture' | 'level'

const LEVEL_CEIL = 3500 // RMS→0..1 归一上限(经验值,决定音浪满格音量;按设备/麦灵敏度可调)

// VAD 调参(照搬小程序)
const PREROLL_FRAMES = 8
const START_HOLD_FRAMES = 3
const END_HOLD_FRAMES = 16
const INITIAL_NOISE_FLOOR = 200
const MIN_NOISE_FLOOR = 50
const MAX_NOISE_FLOOR = 1500
const NOISE_FLOOR_ALPHA = 0.95
const CALIB_FRAMES = 16
const START_MULT = 4.0
const END_MULT = 1.8
const UPDATE_GUARD_MULT = 2.0

type State = 'idle' | 'opening' | 'speaking' | 'finalizing'

export class RealtimeASR {
    private handlers: Partial<Record<ASREvent, (...args: unknown[]) => void>> = {}
    private engine: string
    private shouldRun = false
    private closeEmitted = false

    private ctx: AudioContext | null = null
    private stream: MediaStream | null = null
    private node: AudioWorkletNode | null = null
    private ws: WebSocket | null = null

    private state: State = 'idle'
    private paused = false
    private manual = false // 手动按住说话:接管会话收尾(由按钮松开/上滑决定),压制 VAD 静音自动收尾
    private accText = '' // 本轮累加的已定稿片段(两种模式通用):逐句 sentence_end 不当结束,我们 stop 后 turn_done 才合并发出
    private framesToSend: Int16Array[] = []
    private aboveStartCount = 0
    private belowEndCount = 0

    private noiseFloor = INITIAL_NOISE_FLOOR
    private calibFrames = 0
    private calibSum = 0

    constructor({ engine = '16k_zh_en' }: { engine?: string } = {}) {
        this.engine = engine
    }

    on(event: ASREvent, handler: (...args: unknown[]) => void): this {
        this.handlers[event] = handler
        return this
    }

    async start(): Promise<void> {
        this.shouldRun = true
        this.closeEmitted = false
        this.noiseFloor = INITIAL_NOISE_FLOOR
        this.calibFrames = 0
        this.calibSum = 0
        this.state = 'idle'
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                // autoGainControl 关掉:AGC 会动态归一化音量,扰乱 RMS 自适应阈值;EC/NS 留着降噪
                audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: false },
            })
            // 强制 16k:Chrome 支持;万一没采纳,worklet 的重采样兜底
            this.ctx = new AudioContext({ sampleRate: 16000 })
            await this.ctx.audioWorklet.addModule('/pcm16k-worklet.js')
            const source = this.ctx.createMediaStreamSource(this.stream)
            this.node = new AudioWorkletNode(this.ctx, 'pcm16k')
            this.node.port.onmessage = (e) => this._onFrame(new Int16Array(e.data as ArrayBuffer))
            source.connect(this.node)
            this.node.connect(this.ctx.destination) // worklet 输出静音,只为让 render graph 拉动 process()
        } catch (err) {
            this._emit('error', err)
            this.shouldRun = false
            throw err
        }
    }

    abort(): void {
        this.shouldRun = false
        this.manual = false
        this.accText = ''
        this._closeWs()
        this.state = 'idle'
        this.framesToSend = []
        this.aboveStartCount = 0
        this.belowEndCount = 0
        try {
            this.node?.disconnect()
        } catch {
            /* ignore */
        }
        try {
            this.stream?.getTracks().forEach((t) => t.stop())
        } catch {
            /* ignore */
        }
        try {
            void this.ctx?.close()
        } catch {
            /* ignore */
        }
        this.node = null
        this.stream = null
        this.ctx = null
        this._emitCloseOnce()
    }

    /** 暂停 VAD(半双工 AI 说话期 / 静音):忽略帧、断当前 WS,但麦克风/worklet 不拆。 */
    pause(): void {
        this.paused = true
        this.manual = false
        this.accText = ''
        this._closeWs()
        this.state = 'idle'
        this.framesToSend = []
        this.aboveStartCount = 0
        this.belowEndCount = 0
    }

    resume(): void {
        this.paused = false
        this.state = 'idle'
    }

    /** 按住说话:把"结束一轮"的裁决从 VAD 静音切到松手——置 manual 压制 VAD 自动收尾。
     *  VAD 已在录(speaking/opening)→ 复用会话只接管收尾(沿用已累加文本);没开 → 直接启动。 */
    manualStart(): void {
        if (!this.shouldRun || this.paused) return
        this.manual = true
        this.aboveStartCount = 0
        this.belowEndCount = 0
        if (this.state === 'idle') this._beginSession()
    }

    /** 松开发送:发 stop 让 DashScope 收尾;剩余 final 继续累加(累加是通用的),turn_done 时整段一次性发。
     *  opening 期(WS 未开、无真音频)当取消丢弃避免卡死。 */
    manualStop(): void {
        this.manual = false
        if (this.state === 'opening') {
            this._resetToIdle()
            return
        }
        this._endSession()
    }

    /** 上滑取消:丢弃当前会话,不发送(manual/accText 在 _resetToIdle 清)。 */
    manualCancel(): void {
        this._resetToIdle()
    }

    private _onFrame(frame: Int16Array): void {
        if (!this.shouldRun || this.paused) return
        const energy = computeRMS(frame)
        this._emit('level', Math.min(1, energy / LEVEL_CEIL)) // 实时电平 → UI 音浪(经外部 store,不走状态机)

        if (this.state === 'idle' || this.state === 'opening') {
            this.framesToSend.push(frame)
            // idle 时滑动窗口只留最近 PREROLL_FRAMES;opening 时不截断,继续累积到 WS open
            if (this.state === 'idle' && this.framesToSend.length > PREROLL_FRAMES) this.framesToSend.shift()
        }

        // 冷启动 baseline 校准
        if (this.calibFrames < CALIB_FRAMES) {
            this.calibSum += energy
            this.calibFrames++
            if (this.calibFrames === CALIB_FRAMES) {
                this.noiseFloor = clamp(this.calibSum / CALIB_FRAMES, MIN_NOISE_FLOOR, MAX_NOISE_FLOOR)
            }
            return
        }
        // idle 慢速 EMA 更新 baseline
        if (this.state === 'idle' && energy < this.noiseFloor * UPDATE_GUARD_MULT) {
            this.noiseFloor = clamp(
                NOISE_FLOOR_ALPHA * this.noiseFloor + (1 - NOISE_FLOOR_ALPHA) * energy,
                MIN_NOISE_FLOOR,
                MAX_NOISE_FLOOR,
            )
        }

        const startThreshold = this.noiseFloor * START_MULT
        const endThreshold = this.noiseFloor * END_MULT

        if (this.state === 'idle') {
            if (energy > startThreshold) {
                if (++this.aboveStartCount >= START_HOLD_FRAMES) this._beginSession()
            } else {
                this.aboveStartCount = 0
            }
        } else if (this.state === 'speaking') {
            this._wsSend(frame)
            if (this.manual) {
                // 按住说话:只持续上传,不做静音自动收尾;收尾交给 manualStop/manualCancel
                this.belowEndCount = 0
            } else if (energy < endThreshold) {
                if (++this.belowEndCount >= END_HOLD_FRAMES) this._endSession()
            } else {
                this.belowEndCount = 0
            }
        }
    }

    private _beginSession(): void {
        this.state = 'opening'
        this.aboveStartCount = 0
        this.accText = '' // 新一轮:清空累加(manual 接管已开会话时不走这里,沿用已累加文本)
        this._emit('capture', true) // 会话开:UI 音浪指示"正在录"(VAD 自动 / 手动按住都经这里)
        const ws = new WebSocket(api.asr.wsUrl(this.engine))
        ws.binaryType = 'arraybuffer'
        this.ws = ws
        ws.onopen = () => {
            // server 会把音频缓冲到 DashScope task-started,所以这里 WS open 即 drain pre-roll + 进 speaking
            for (const f of this.framesToSend) this._wsSend(f)
            this.framesToSend = []
            this.state = 'speaking'
            this.belowEndCount = 0
        }
        ws.onmessage = (e) => this._onWsMessage(e.data)
        ws.onerror = () => {
            this._emit('error', new Error('asr ws error'))
            this._resetToIdle()
        }
        ws.onclose = () => {
            this.ws = null
            this.state = 'idle'
            this.belowEndCount = 0
            this._emit('capture', false) // 会话关:熄灭 UI 音浪。覆盖所有关闭路径(server 收尾 / _closeWs / 错误)
        }
    }

    private _endSession(): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            this.state = 'idle'
            return
        }
        this.state = 'finalizing'
        try {
            this.ws.send(JSON.stringify({ type: 'stop' }))
        } catch {
            /* ignore */
        }
        this.belowEndCount = 0
    }

    private _wsSend(frame: Int16Array): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            // 发底层字节(16k PCM 小端)。worklet 来的是普通 ArrayBuffer,cast 收口新版 TS 的
            // ArrayBufferLike(含 SharedArrayBuffer)不满足 BufferSource 的报错
            this.ws.send(frame.buffer as ArrayBuffer)
        }
    }

    private _onWsMessage(data: unknown): void {
        if (typeof data !== 'string') return
        let msg: { type?: string; text?: string; message?: string; code?: string }
        try {
            msg = JSON.parse(data)
        } catch {
            return
        }
        // 累加对两种模式通用:DashScope 逐句 sentence_end 一律不当"结束",只累加 + 实时显示;
        // 真正的发送只在"我们自己 stop(VAD 1s 静默 / 松手)→ DashScope turn_done"时,整段一次性发。
        if (msg.type === 'partial') {
            const t = msg.text ?? ''
            // 实时显示 = 已累加片段 + 当前在识别的半句(不写回 accText)
            this._emit('partial', this._joinAcc(t))
        } else if (msg.type === 'final') {
            // sentence_end:累加成已定稿片段、以 partial 实时显示,不触发 LLM
            this.accText = this._joinAcc(msg.text ?? '')
            this._emit('partial', this.accText)
        } else if (msg.type === 'turn_done') {
            // 收尾:DashScope 把剩余识别 + task-finished 推完 → 此刻才把整段一次性发出去触发 LLM
            const full = this.accText.trim()
            this.accText = ''
            if (full) this._emit('final', full)
            this._closeWs()
        } else if (msg.type === 'error') {
            // NO_VALID_AUDIO = VAD 误触发的空会话(噪声尖峰,没真说话),瞬时无害:
            // 静默回 idle 继续监听,绝不 abort(否则一次噪声就把整个通话关了)。其它 fatal 才 abort 防死循环。
            const benign = msg.code === 'NO_VALID_AUDIO_ERROR' || /no_valid_audio/i.test(msg.message ?? '')
            if (benign) {
                this._resetToIdle()
            } else {
                this._emit('error', new Error(msg.message ?? 'asr error'))
                this.abort()
            }
        }
    }

    // 累加已定稿片段与当前半句:句间用空格(标点已开,中文多一个空格无害,英文反而需要)
    private _joinAcc(t: string): string {
        return (this.accText ? `${this.accText} ${t}` : t).trim()
    }

    private _resetToIdle(): void {
        this.manual = false
        this.accText = ''
        this._closeWs()
        this.state = 'idle'
        this.framesToSend = []
    }

    private _closeWs(): void {
        if (!this.ws) return
        try {
            this.ws.close()
        } catch {
            /* ignore */
        }
        this.ws = null
    }

    private _emitCloseOnce(): void {
        if (this.closeEmitted) return
        this.closeEmitted = true
        this._emit('close')
    }

    private _emit(event: ASREvent, ...args: unknown[]): void {
        const h = this.handlers[event]
        if (h) {
            try {
                h(...args)
            } catch (e) {
                console.error('[asr] handler threw', e)
            }
        }
    }
}

function clamp(x: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, x))
}

function computeRMS(samples: Int16Array): number {
    const len = samples.length
    if (!len) return 0
    let sum = 0
    for (let i = 0; i < len; i++) sum += samples[i] * samples[i]
    return Math.sqrt(sum / len)
}
